import {
	Button,
	Card,
	Col,
	Empty,
	Form,
	Input,
	Popconfirm,
	Row,
	Select,
	Tag,
	Typography
} from 'antd'
import {
	DeleteOutlined,
	EditOutlined,
	NodeIndexOutlined,
	PlusOutlined,
	SaveOutlined
} from '@ant-design/icons'
import { FC, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { relayStore } from '../store/relay'

interface Props {
	charType: 'mechanic' | 'electric'
	accentColor: string
}

export const ChecksTypeTable: FC<Props> = ({
	charType,
	accentColor
}) => {
	const {
		relayTypes,
		createMechanic,
		deleteMechanic,
		createElectric,
		updateMechanic,
		updateElectric,
		deleteElectric
	} = relayStore()

	const [form] = Form.useForm()
	const [selectedRelayTypeId, setSelectedRelayTypeId] = useState<
		string | undefined
	>(undefined)
	const [showForm, setShowForm] = useState(false)
	const formRef = useRef<HTMLDivElement>(null)

	const [searchParams] = useSearchParams()

	const isMechanic = charType === 'mechanic'
	const dataKey = isMechanic ? 'mechanics' : 'electrics'

	const selectedRelayType = relayTypes.find(
		(rt) => rt.id === selectedRelayTypeId
	)
	const characteristics = selectedRelayType?.[dataKey] ?? []

	// Auto-open form if ?add=true is in the URL
	useEffect(() => {
		if (searchParams.get('add') === 'true') {
			setShowForm(true)
		}
	}, [searchParams])

	// Scroll to form when opened
	useEffect(() => {
		if (showForm && formRef.current) {
			formRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			})
		}
	}, [showForm])

	const handleDelete = (relayTypeId: string, id: string) => {
		if (isMechanic) {
			deleteMechanic(relayTypeId, id)
		} else {
			deleteElectric(relayTypeId, id)
		}
	}

	const handleSubmit = (values: any) => {
		if (values.id) {
			const updatedValues = {
				...values,
				relayTypeId: selectedRelayTypeId
			}
			if (isMechanic) {
				updateMechanic(updatedValues)
			} else {
				updateElectric(updatedValues)
			}
			setShowForm(false)
		} else {
			if (isMechanic) {
				createMechanic({
					...values,
					relayTypeId: selectedRelayTypeId
				})
			} else {
				createElectric({
					...values,
					relayTypeId: selectedRelayTypeId
				})
			}
		}
		form.resetFields()
	}

	const handleEdit = (item: any) => {
		console.log(item)
		form.setFieldsValue({
			...item,
			parentId: item.parentId ?? null,
			id: item.id
		})
		setShowForm(true)
	}

	const getParentName = (parentId: string | null | undefined) => {
		if (!parentId) return null
		return characteristics.find((c) => c.id === parentId)?.name
	}

	return (
		<div>
			{/* Relay type selector */}
			<Card
				style={{ borderRadius: 12, marginBottom: 20 }}
				styles={{ body: { padding: '20px 24px' } }}>
				<Typography.Text
					strong
					style={{
						display: 'block',
						marginBottom: 8,
						fontSize: 15
					}}>
					Выберите тип реле
				</Typography.Text>
				<Select
					showSearch
					size="large"
					placeholder="Поиск типа реле..."
					value={selectedRelayTypeId}
					onChange={(v) => {
						setSelectedRelayTypeId(v)
						form.resetFields()
					}}
					filterOption={(input, option) =>
						(option?.label ?? '')
							.toLowerCase()
							.includes(input.toLowerCase())
					}
					options={relayTypes.map((rt) => ({
						value: rt.id,
						label: rt.name
					}))}
					style={{ width: '100%', maxWidth: 480 }}
				/>
			</Card>

			{/* No relay type selected state */}
			{!selectedRelayType && (
				<Card
					style={{
						borderRadius: 12,
						textAlign: 'center',
						padding: '40px 0'
					}}>
					<Empty
						description="Выберите тип реле для просмотра характеристик"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				</Card>
			)}

			{/* Existing characteristics */}
			{selectedRelayType && (
				<>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: 16
						}}>
						<Typography.Title
							level={5}
							style={{ margin: 0 }}>
							Характеристики — {selectedRelayType.name}
							<Tag
								color={accentColor}
								style={{
									marginLeft: 10,
									fontSize: 13
								}}>
								{characteristics.length}
							</Tag>
						</Typography.Title>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => setShowForm(true)}>
							Добавить
						</Button>
					</div>

					{characteristics.length === 0 ? (
						<Card
							style={{
								borderRadius: 12,
								textAlign: 'center',
								padding: '32px 0',
								marginBottom: 20
							}}>
							<Empty
								description="Характеристик пока нет"
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								style={{ marginTop: 16 }}
								onClick={() => setShowForm(true)}>
								Добавить первую
							</Button>
						</Card>
					) : (
						<Row
							gutter={[12, 12]}
							style={{ marginBottom: 24 }}>
							{[...characteristics]
								.sort(
									(a, b) =>
										a.orderInLevel -
										b.orderInLevel
								)
								.map((item) => {
									const parentName = getParentName(
										item.parentId
									)
									return (
										<Col
											xs={24}
											sm={12}
											lg={8}
											key={item.id}>
											<Card
												size="small"
												style={{
													borderRadius: 10,
													borderLeft: `3px solid ${accentColor}`,
													height: '100%'
												}}
												styles={{
													body: {
														padding:
															'14px 16px',
														display:
															'flex',
														flexDirection:
															'column',
														justifyContent:
															'space-between',
														height: '100%'
													}
												}}>
												<div>
													{parentName && (
														<div
															style={{
																fontSize: 12,
																color: '#999',
																marginBottom: 2,
																display:
																	'flex',
																alignItems:
																	'center',
																gap: 4
															}}>
															<NodeIndexOutlined />
															{
																parentName
															}
														</div>
													)}
													<Typography.Text
														strong
														style={{
															fontSize: 15
														}}>
														{item.name}
													</Typography.Text>
												</div>

												<div
													style={{
														display:
															'flex',
														alignItems:
															'center',
														justifyContent:
															'space-between',
														marginTop: 10
													}}>
													<div
														style={{
															display:
																'flex',
															gap: 6,
															flexWrap:
																'wrap'
														}}>
														{item.measurement && (
															<Tag color="blue">
																{
																	item.measurement
																}
															</Tag>
														)}
														<Tag>
															ур.{' '}
															{
																item.level
															}
														</Tag>
														<Tag>
															#
															{
																item.orderInLevel
															}
														</Tag>
													</div>
													<div
														style={{
															display:
																'flex',
															gap: 4
														}}>
														<Button
															type="text"
															size="small"
															icon={
																<EditOutlined
																	style={{
																		color: accentColor
																	}}
																/>
															}
															onClick={() => {
																handleEdit(
																	item
																)
															}}
														/>
														<Popconfirm
															title="Удалить характеристику?"
															onConfirm={() =>
																handleDelete(
																	item.relayTypeId,
																	item.id
																)
															}>
															<Button
																danger
																type="text"
																size="small"
																icon={
																	<DeleteOutlined />
																}
															/>
														</Popconfirm>
													</div>
												</div>
											</Card>
										</Col>
									)
								})}
						</Row>
					)}
				</>
			)}

			{/* Add form */}
			{showForm && selectedRelayType && (
				<div ref={formRef}>
					<Card
						title={
							isMechanic
								? 'Новая механическая характеристика'
								: 'Новая электрическая характеристика'
						}
						style={{
							borderRadius: 12,
							borderTop: `3px solid ${accentColor}`
						}}>
						<Form
							form={form}
							layout="vertical"
							variant="filled"
							onFinish={handleSubmit}>
							<Row gutter={16}>
								<Col xs={24} sm={12}>
									<Form.Item
										label="Название"
										name="name"
										rules={[
											{
												required: true,
												message:
													'Введите название'
											}
										]}>
										<Input size="large" />
									</Form.Item>
								</Col>
								<Col xs={24} sm={12}>
									<Form.Item
										label="Единица измерения"
										name="measurement"
										rules={[
											{
												required: true,
												message:
													'Введите или выберите единицу измерения'
											}
										]}>
										<Input
											size="large"
											placeholder="мм, Н, с, мА, Ом и т.д."
										/>
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={16}>
								<Col xs={24} sm={8}>
									<Form.Item
										label="Уровень"
										name="level"
										rules={[
											{
												required: true,
												message:
													'Введите уровень'
											}
										]}>
										<Input
											type="number"
											size="large"
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={8}>
									<Form.Item
										label="Порядок"
										name="order"
										rules={[
											{
												required: true,
												message:
													'Введите порядок'
											}
										]}>
										<Input
											type="number"
											size="large"
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={8}>
									<Form.Item
										label="Порядок в уровне"
										name="orderInLevel"
										rules={[
											{
												required: true,
												message:
													'Введите порядок'
											}
										]}>
										<Input
											type="number"
											size="large"
										/>
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={16}>
								<Col xs={24} sm={12}>
									<Form.Item
										label="Диапазон колонок"
										name="colspan"
										rules={[
											{
												required: true,
												message:
													'Введите диапазон'
											}
										]}>
										<Input
											type="number"
											size="large"
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={12}>
									<Form.Item
										label="Диапазон строк"
										name="rowspan"
										rules={[
											{
												required: true,
												message:
													'Введите диапазон'
											}
										]}>
										<Input
											type="number"
											size="large"
										/>
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={16}>
								<Col xs={24} sm={8}>
									<Form.Item
										label="Принимает значение"
										name="isAcceptsValue">
										<Select size="large">
											<Select.Option
												value={true}>
												Да
											</Select.Option>
											<Select.Option
												value={false}>
												Нет
											</Select.Option>
										</Select>
									</Form.Item>
								</Col>
								<Col xs={24} sm={8}>
									<Form.Item
										label={
											isMechanic
												? 'Вертикальный механизм'
												: 'Вертикальный'
										}
										name="vertical">
										<Select size="large">
											<Select.Option
												value={true}>
												Да
											</Select.Option>
											<Select.Option
												value={false}>
												Нет
											</Select.Option>
										</Select>
									</Form.Item>
								</Col>
								<Col xs={24} sm={8}>
									<Form.Item
										label={
											isMechanic
												? 'Родительский механизм'
												: 'Родительская хар.'
										}
										name="parentId">
										<Select
											size="large"
											defaultValue={null}>
											<Select.Option
												value={null}>
												Нет
											</Select.Option>
											{[...characteristics]
												.sort(
													(a, b) =>
														a.orderInLevel -
														b.orderInLevel
												)
												.map((item) => (
													<Select.Option
														key={item.id}
														value={
															item.id
														}>
														{item.parentId
															? getParentName(
																	item.parentId
																) +
																' / '
															: ''}
														{item.name}
													</Select.Option>
												))}
										</Select>
									</Form.Item>
								</Col>
								<Col xs={24} sm={12}>
									<Form.Item
										label="ID (для редактирования)"
										hidden
										name="id">
										<Input size="large" />
									</Form.Item>
								</Col>
							</Row>

							<div
								style={{
									display: 'flex',
									gap: 12,
									paddingTop: 8
								}}>
								<Button
									type="primary"
									htmlType="submit"
									icon={<SaveOutlined />}
									size="large">
									Сохранить
								</Button>
								<Button
									size="large"
									onClick={() => {
										setShowForm(false)
										form.resetFields()
									}}>
									Отмена
								</Button>
							</div>
						</Form>
					</Card>
				</div>
			)}
		</div>
	)
}
